import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, Plus, Image, Tag, ShoppingCart, Send, 
  Shield, RefreshCw, Sparkles, X
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../App";

const CHAIN_CONFIG = {
  xrp: { name: "XRP", color: "#23292F", icon: "🌊" },
  sol: { name: "Solana", color: "#9945FF", icon: "☀️" },
  xlm: { name: "Stellar", color: "#14B8E0", icon: "⭐" },
  hbar: { name: "HBAR", color: "#3A3A3A", icon: "⬡" },
  btc: { name: "Bitcoin", color: "#F7931A", icon: "₿" },
  eth: { name: "Ethereum", color: "#627EEA", icon: "⟠" },
  link: { name: "Chainlink", color: "#375BD2", icon: "⬡" }
};

const NFTGallery = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("my-nfts");
  const [myNFTs, setMyNFTs] = useState([]);
  const [marketplaceNFTs, setMarketplaceNFTs] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Dialogs
  const [showMintDialog, setShowMintDialog] = useState(false);
  const [showListDialog, setShowListDialog] = useState(false);
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState(null);
  
  // Forms
  const [mintForm, setMintForm] = useState({
    wallet_id: "",
    name: "",
    description: "",
    image_url: ""
  });
  const [listPrice, setListPrice] = useState("");
  const [buyWalletId, setBuyWalletId] = useState("");

  const getAuthConfig = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const fetchData = async () => {
    try {
      const config = getAuthConfig();
      const [nftsRes, marketRes, walletsRes] = await Promise.all([
        axios.get(`${API}/nfts`, config),
        axios.get(`${API}/nfts/marketplace`, config),
        axios.get(`${API}/wallets`, config)
      ]);
      
      setMyNFTs(nftsRes.data.nfts || []);
      setMarketplaceNFTs(marketRes.data.nfts || []);
      setWallets(walletsRes.data.wallets || []);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed!");
  };

  const handleMint = async (e) => {
    e.preventDefault();
    if (!mintForm.wallet_id || !mintForm.name || !mintForm.image_url) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const config = getAuthConfig();
      const response = await axios.post(`${API}/nfts/mint`, mintForm, config);
      toast.success(`NFT "${response.data.name}" minted successfully!`);
      setShowMintDialog(false);
      setMintForm({ wallet_id: "", name: "", description: "", image_url: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mint NFT");
    }
  };

  const handleList = async () => {
    if (!selectedNFT || !listPrice || parseFloat(listPrice) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }
    
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/nfts/${selectedNFT.nft_id}/list`, {
        nft_id: selectedNFT.nft_id,
        price: parseFloat(listPrice)
      }, config);
      toast.success("NFT listed for sale!");
      setShowListDialog(false);
      setSelectedNFT(null);
      setListPrice("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to list NFT");
    }
  };

  const handleUnlist = async (nft) => {
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/nfts/${nft.nft_id}/unlist`, {}, config);
      toast.success("NFT removed from sale");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlist NFT");
    }
  };

  const handleBuy = async () => {
    if (!selectedNFT || !buyWalletId) {
      toast.error("Please select a wallet");
      return;
    }
    
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/nfts/${selectedNFT.nft_id}/buy`, {
        nft_id: selectedNFT.nft_id,
        buyer_wallet_id: buyWalletId
      }, config);
      toast.success(`NFT "${selectedNFT.name}" purchased!`);
      setShowBuyDialog(false);
      setSelectedNFT(null);
      setBuyWalletId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to buy NFT");
    }
  };

  const handleSeedNFTs = async () => {
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/demo/seed-nfts`, {}, config);
      toast.success("Demo NFTs created!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create demo NFTs");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#FFD700] font-medium tracking-wider">LOADING NFTs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#000A1F] to-[#001133]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#020617]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white/60 hover:text-white"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#FFD700]" />
                <h1 className="text-xl font-bold text-white font-['Syne']">NFT Gallery</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                className="text-white/60 hover:text-white"
                disabled={refreshing}
                data-testid="refresh-nfts-btn"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              <Button
                onClick={handleSeedNFTs}
                variant="outline"
                className="btn-outline-gold rounded-lg"
                data-testid="seed-nfts-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Demo NFTs
              </Button>
              
              <Dialog open={showMintDialog} onOpenChange={setShowMintDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-gold rounded-lg" data-testid="mint-nft-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Mint NFT
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-card border-[#FFD700]/20">
                  <DialogHeader>
                    <DialogTitle className="text-white font-['Syne']">Mint New NFT</DialogTitle>
                    <DialogDescription className="text-white/50">
                      Create a new cryptographically signed NFT on your chosen chain
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleMint} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-white/70">Select Wallet</Label>
                      <Select
                        value={mintForm.wallet_id}
                        onValueChange={(value) => setMintForm(prev => ({ ...prev, wallet_id: value }))}
                      >
                        <SelectTrigger className="bg-[#000A1F] border-[#FFD700]/20 text-white" data-testid="mint-wallet-select">
                          <SelectValue placeholder="Select wallet to mint on" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B1221] border-[#FFD700]/20">
                          {wallets.map((wallet) => (
                            <SelectItem key={wallet.wallet_id} value={wallet.wallet_id} className="text-white">
                              {CHAIN_CONFIG[wallet.chain]?.icon} {wallet.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">NFT Name *</Label>
                      <Input
                        value={mintForm.name}
                        onChange={(e) => setMintForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="My Awesome NFT"
                        className="input-quantum"
                        required
                        data-testid="mint-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Description</Label>
                      <Textarea
                        value={mintForm.description}
                        onChange={(e) => setMintForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe your NFT..."
                        className="input-quantum min-h-[80px]"
                        data-testid="mint-desc-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70">Image URL *</Label>
                      <Input
                        value={mintForm.image_url}
                        onChange={(e) => setMintForm(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="https://..."
                        className="input-quantum"
                        required
                        data-testid="mint-image-input"
                      />
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-[#00FFFF]/10 border border-[#00FFFF]/30">
                      <Shield className="w-4 h-4 text-[#00FFFF]" />
                      <span className="text-[#00FFFF] text-sm">NFT will be cryptographically signed</span>
                    </div>
                    <Button type="submit" className="w-full btn-gold rounded-lg" data-testid="mint-submit-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Mint NFT
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 p-1 rounded-full mb-8">
            <TabsTrigger value="my-nfts" className="tab-quantum" data-testid="tab-my-nfts">
              <Image className="w-4 h-4 mr-2" />
              My NFTs ({myNFTs.length})
            </TabsTrigger>
            <TabsTrigger value="marketplace" className="tab-quantum" data-testid="tab-marketplace">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Marketplace ({marketplaceNFTs.length})
            </TabsTrigger>
          </TabsList>

          {/* My NFTs Tab */}
          <TabsContent value="my-nfts" className="mt-0">
            {myNFTs.length === 0 ? (
              <Card className="glass-card p-12 text-center">
                <Sparkles className="w-16 h-16 text-[#FFD700]/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No NFTs Yet</h3>
                <p className="text-white/50 mb-6">Mint your first NFT or add demo NFTs to get started.</p>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => setShowMintDialog(true)} className="btn-gold rounded-lg">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Mint NFT
                  </Button>
                  <Button onClick={handleSeedNFTs} variant="outline" className="btn-outline-gold rounded-lg">
                    Add Demo NFTs
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {myNFTs.map((nft, index) => (
                  <NFTCard
                    key={nft.nft_id}
                    nft={nft}
                    index={index}
                    isOwned={true}
                    onList={() => {
                      setSelectedNFT(nft);
                      setShowListDialog(true);
                    }}
                    onUnlist={() => handleUnlist(nft)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Marketplace Tab */}
          <TabsContent value="marketplace" className="mt-0">
            {marketplaceNFTs.length === 0 ? (
              <Card className="glass-card p-12 text-center">
                <ShoppingCart className="w-16 h-16 text-[#FFD700]/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No NFTs Listed</h3>
                <p className="text-white/50">Check back later for NFTs available for purchase.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {marketplaceNFTs.map((nft, index) => (
                  <NFTCard
                    key={nft.nft_id}
                    nft={nft}
                    index={index}
                    isOwned={false}
                    onBuy={() => {
                      setSelectedNFT(nft);
                      setShowBuyDialog(true);
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* List Dialog */}
        <Dialog open={showListDialog} onOpenChange={setShowListDialog}>
          <DialogContent className="glass-card border-[#FFD700]/20">
            <DialogHeader>
              <DialogTitle className="text-white font-['Syne']">List NFT for Sale</DialogTitle>
              <DialogDescription className="text-white/50">
                Set a price to list "{selectedNFT?.name}" on the marketplace
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white/70">
                  Price ({selectedNFT && CHAIN_CONFIG[selectedNFT.chain]?.name})
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={listPrice}
                  onChange={(e) => setListPrice(e.target.value)}
                  placeholder="0.00"
                  className="input-quantum"
                  data-testid="list-price-input"
                />
              </div>
              <Button onClick={handleList} className="w-full btn-gold rounded-lg" data-testid="list-submit-btn">
                <Tag className="w-4 h-4 mr-2" />
                List for Sale
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Buy Dialog */}
        <Dialog open={showBuyDialog} onOpenChange={setShowBuyDialog}>
          <DialogContent className="glass-card border-[#FFD700]/20">
            <DialogHeader>
              <DialogTitle className="text-white font-['Syne']">Buy NFT</DialogTitle>
              <DialogDescription className="text-white/50">
                Purchase "{selectedNFT?.name}" for {selectedNFT?.listing_price} {selectedNFT && CHAIN_CONFIG[selectedNFT.chain]?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {selectedNFT && (
                <div className="p-4 rounded-xl bg-black/30">
                  <img
                    src={selectedNFT.image_url}
                    alt={selectedNFT.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <p className="text-white font-semibold">{selectedNFT.name}</p>
                  <p className="text-[#FFD700] text-xl font-bold">
                    {selectedNFT.listing_price} {CHAIN_CONFIG[selectedNFT.chain]?.name}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-white/70">Pay with Wallet</Label>
                <Select value={buyWalletId} onValueChange={setBuyWalletId}>
                  <SelectTrigger className="bg-[#000A1F] border-[#FFD700]/20 text-white" data-testid="buy-wallet-select">
                    <SelectValue placeholder="Select wallet" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1221] border-[#FFD700]/20">
                    {wallets
                      .filter(w => selectedNFT && w.chain === selectedNFT.chain)
                      .map((wallet) => (
                        <SelectItem key={wallet.wallet_id} value={wallet.wallet_id} className="text-white">
                          {wallet.name} ({wallet.balance} {CHAIN_CONFIG[wallet.chain]?.name})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleBuy} className="w-full btn-gold rounded-lg" data-testid="buy-submit-btn">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Buy Now
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// NFT Card Component
const NFTCard = ({ nft, index, isOwned, onList, onUnlist, onBuy }) => {
  const config = CHAIN_CONFIG[nft.chain];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="glass-card overflow-hidden group hover:border-[#FFD700]/30 transition-all" data-testid={`nft-card-${nft.nft_id}`}>
        <div className="relative">
          <img
            src={nft.image_url}
            alt={nft.name}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=400";
            }}
          />
          <div className="absolute top-2 right-2">
            <Badge className="bg-black/60 text-white border-0">
              {config?.icon} {config?.name}
            </Badge>
          </div>
          {nft.is_listed && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-[#FFD700] text-[#002366] border-0">
                FOR SALE
              </Badge>
            </div>
          )}
          <div className="absolute bottom-2 right-2">
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#00FFFF]/20 border border-[#00FFFF]/30">
              <Shield className="w-3 h-3 text-[#00FFFF]" />
              <span className="text-[#00FFFF] text-xs">Verified</span>
            </div>
          </div>
        </div>
        
        <CardContent className="p-4">
          <h3 className="text-white font-semibold truncate mb-1">{nft.name}</h3>
          <p className="text-white/40 text-xs truncate mb-3">{nft.description || "No description"}</p>
          
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/40 text-xs">Token ID</span>
            <code className="text-white/60 text-xs">{nft.token_id}</code>
          </div>
          
          {nft.is_listed && (
            <div className="flex items-center justify-between mb-3 p-2 rounded-lg bg-[#FFD700]/10">
              <span className="text-white/60 text-sm">Price</span>
              <span className="text-[#FFD700] font-bold">
                {nft.listing_price} {config?.name}
              </span>
            </div>
          )}
          
          {isOwned ? (
            <div className="flex gap-2">
              {nft.is_listed ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={onUnlist}
                  data-testid={`unlist-btn-${nft.nft_id}`}
                >
                  <X className="w-4 h-4 mr-1" />
                  Unlist
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
                  onClick={onList}
                  data-testid={`list-btn-${nft.nft_id}`}
                >
                  <Tag className="w-4 h-4 mr-1" />
                  List
                </Button>
              )}
            </div>
          ) : (
            <Button
              className="w-full btn-gold rounded-lg"
              onClick={onBuy}
              data-testid={`buy-btn-${nft.nft_id}`}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Buy Now
            </Button>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default NFTGallery;
